import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    $getNearestNodeFromDOMNode,
    $getNodeByKey,
    $getRoot,
} from "lexical";
import { $isTableNode } from "@lexical/table";
import { toast } from "react-toastify";

// Adjust this import to match your project
import { $isImageNode } from "../nodes/ImageNode";

function getTableElement(target) {
    return target instanceof Element ? target.closest("table") : null;
}

function getImageElement(target) {
    return target instanceof Element ? target.closest("img") : null;
}

function findMatchingLexicalNode(startNode, matcher) {
    let node = startNode;

    while (node) {
        if (matcher(node)) {
            return node;
        }

        if (typeof node.getParent === "function") {
            node = node.getParent();
        } else {
            node = null;
        }
    }

    return null;
}

async function copyTextAndHtml({ html, text }) {
    try {
        if (navigator.clipboard && navigator.clipboard.write && window.ClipboardItem) {
            const clipboardItem = new ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([text], { type: "text/plain" }),
            });
            await navigator.clipboard.write([clipboardItem]);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text || html);
        }
    } catch (err) {
        console.error("Failed to copy table to clipboard", err);

        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text || html);
        } else {
            throw err;
        }
    }
}

async function copyImageToClipboard(src) {
    if (!navigator.clipboard || !navigator.clipboard.write) {
        throw new Error("Clipboard image API not supported");
    }

    const response = await fetch(src);
    const blob = await response.blob();

    const item = new ClipboardItem({
        [blob.type || "image/png"]: blob,
    });

    await navigator.clipboard.write([item]);
}

export default function HoverNodeActionsPlugin({ isEditable }) {
    const [editor] = useLexicalComposerContext();

    const [state, setState] = useState({
        visible: false,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        type: null,
        nodeKey: null,
        imageSrc: null,
        tableElement: null,
    });

    const overlayRef = useRef(null);
    const hideTimeout = useRef(null);
    const stateRef = useRef(state);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const clearHideTimeout = () => {
        if (hideTimeout.current) {
            window.clearTimeout(hideTimeout.current);
            hideTimeout.current = null;
        }
    };

    const hideOverlay = () => {
        clearHideTimeout();

        setState((prev) => ({
            ...prev,
            visible: false,
            type: null,
            nodeKey: null,
            imageSrc: null,
            tableElement: null,
        }));
    };

    const scheduleHideOverlay = () => {
        clearHideTimeout();
        hideTimeout.current = window.setTimeout(() => {
            hideOverlay();
        }, 100);
    };

    const showOverlay = ({ type, element, nodeKey, imageSrc = null, tableElement = null }) => {
        if (!element) return;

        const rect = element.getBoundingClientRect();

        setState({
            visible: true,
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
            type,
            nodeKey,
            imageSrc,
            tableElement,
        });

        clearHideTimeout();
    };

    const resolveHoveredNodeInfo = (element, type) => {
        let result = null;

        editor.read(() => {
            const lexicalNode = $getNearestNodeFromDOMNode(element);
            if (!lexicalNode) return;

            if (type === "table") {
                const tableNode = findMatchingLexicalNode(lexicalNode, $isTableNode);
                if (!tableNode) return;

                result = {
                    type: "table",
                    nodeKey: tableNode.getKey(),
                    imageSrc: null,
                    tableElement: element,
                };
                return;
            }

            if (type === "image") {
                const imageNode = findMatchingLexicalNode(lexicalNode, $isImageNode);
                if (!imageNode) return;

                result = {
                    type: "image",
                    nodeKey: imageNode.getKey(),
                    imageSrc: typeof imageNode.getSrc === "function" ? imageNode.getSrc() : element.src,
                    tableElement: null,
                };
            }
        });

        return result;
    };

    useLayoutEffect(() => {
        if (!isEditable) return undefined;

        let currentCleanup = null;

        const setupListeners = () => {
            if (currentCleanup) {
                currentCleanup();
            }

            const handleDocumentMouseMove = (event) => {
                const target = event.target;
                const tableEl = getTableElement(target);
                const imgEl = getImageElement(target);

                const isInOverlay =
                    !!overlayRef.current &&
                    target instanceof Node &&
                    overlayRef.current.contains(target);

                if (tableEl) {
                    const info = resolveHoveredNodeInfo(tableEl, "table");
                    if (info?.nodeKey) {
                        showOverlay({
                            type: "table",
                            element: tableEl,
                            nodeKey: info.nodeKey,
                            tableElement: tableEl,
                        });
                    }
                    return;
                }

                if (imgEl) {
                    const info = resolveHoveredNodeInfo(imgEl, "image");
                    if (info?.nodeKey) {
                        showOverlay({
                            type: "image",
                            element: imgEl,
                            nodeKey: info.nodeKey,
                            imageSrc: info.imageSrc,
                        });
                    }
                    return;
                }

                if (isInOverlay) {
                    clearHideTimeout();
                    return;
                }

                if (stateRef.current.visible) {
                    scheduleHideOverlay();
                }
            };

            const handleDocumentMouseDown = (event) => {
                const target = event.target;

                const isInOverlay =
                    !!overlayRef.current &&
                    target instanceof Node &&
                    overlayRef.current.contains(target);

                if (isInOverlay) return;

                const tableEl = getTableElement(target);
                const imgEl = getImageElement(target);

                if (!tableEl && !imgEl) {
                    hideOverlay();
                }
            };

            const repositionOverlay = () => {
                const currentState = stateRef.current;

                if (!currentState.visible) return;

                if (currentState.type === "table" && currentState.tableElement?.isConnected) {
                    showOverlay({
                        type: "table",
                        element: currentState.tableElement,
                        nodeKey: currentState.nodeKey,
                        tableElement: currentState.tableElement,
                    });
                    return;
                }

                if (currentState.type === "image") {
                    const img = currentState.nodeKey
                        ? document.querySelector(`img`)
                        : null;

                    if (img && img.isConnected) {
                        const info = resolveHoveredNodeInfo(img, "image");
                        if (info?.nodeKey === currentState.nodeKey) {
                            showOverlay({
                                type: "image",
                                element: img,
                                nodeKey: info.nodeKey,
                                imageSrc: info.imageSrc,
                            });
                            return;
                        }
                    }

                    hideOverlay();
                }
            };

            document.addEventListener("mousemove", handleDocumentMouseMove);
            document.addEventListener("mousedown", handleDocumentMouseDown);
            window.addEventListener("scroll", repositionOverlay, true);
            window.addEventListener("resize", repositionOverlay);

            currentCleanup = () => {
                document.removeEventListener("mousemove", handleDocumentMouseMove);
                document.removeEventListener("mousedown", handleDocumentMouseDown);
                window.removeEventListener("scroll", repositionOverlay, true);
                window.removeEventListener("resize", repositionOverlay);
                clearHideTimeout();
            };
        };

        const initialRoot = editor.getRootElement();
        if (initialRoot) {
            setupListeners();
        }

        const unregister = editor.registerRootListener((rootElement) => {
            if (rootElement) {
                setupListeners();
            } else if (currentCleanup) {
                currentCleanup();
                currentCleanup = null;
            }
        });

        return () => {
            unregister();
            if (currentCleanup) {
                currentCleanup();
            }
        };
    }, [editor, isEditable]);

    const deleteNode = () => {
        if (!state.nodeKey) return;

        let deletedType = null;

        editor.update(() => {
            const node = $getNodeByKey(state.nodeKey);
            if (!node) return;

            if ($isTableNode(node)) {
                node.remove();
                deletedType = "table";
                return;
            }

            if ($isImageNode(node)) {
                node.remove();
                deletedType = "image";
            }
        });

        if (deletedType === "table") {
            toast.success("Table deleted");
        } else if (deletedType === "image") {
            toast.success("Image deleted");
        } else {
            toast.error("Unable to delete item");
        }

        hideOverlay();
    };

    const copyNode = async () => {
        try {
            if (state.type === "table" && state.tableElement) {
                const html = state.tableElement.outerHTML;
                const text = state.tableElement.innerText;
                await copyTextAndHtml({ html, text });
                toast.success("Table copied to clipboard");
                return;
            }

            if (state.type === "image" && state.imageSrc) {
                try {
                    await copyImageToClipboard(state.imageSrc);
                    toast.success("Image copied to clipboard");
                } catch (err) {
                    console.error("Failed to copy image to clipboard", err);

                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(state.imageSrc);
                        toast.success("Image URL copied to clipboard");
                    } else {
                        toast.error("Unable to copy image");
                    }
                }
            }
        } catch (err) {
            console.error("Copy error:", err);
            toast.error("Failed to copy");
        }
    };

    const wrapperStyle = useMemo(() => {
        if (!state.visible) {
            return { display: "none" };
        }

        return {
            position: "fixed",
            top: `${state.y - 30}px`,
            left: `${state.x}px`,
            zIndex: 10000,
            display: "flex",
            gap: "0.25rem",
            background: "var(--bg-color-contrast1)",
            border: "1px solid var(--border-color)",
            borderRadius: "0.25rem",
            boxShadow: "0 0 12px rgba(0,0,0,0.3)",
            padding: "0.35rem 0.5rem",
            pointerEvents: "auto",
        };
    }, [state]);

    if (!isEditable) {
        return null;
    }

    return (
        <div
            ref={overlayRef}
            className="hover-node-actions"
            style={wrapperStyle}
            onMouseEnter={clearHideTimeout}
            onMouseLeave={scheduleHideOverlay}
        >
            <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={copyNode}
                title="Copy"
            >
                <i className="bi bi-clipboard" />
            </button>

            <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={deleteNode}
                title="Delete"
            >
                <i className="bi bi-trash" />
            </button>
        </div>
    );
}